import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { marker } from '@common/translate-marker';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { NominatedAgentsSaveOrganizationsSaveModel } from '../../vessel-visit/vessel-visit-list/models/NominatedAgents/NominatedAgentsSaveOrganizations.save.model';

@Injectable({
    providedIn: 'root'
})
export class NominatedAgentService {
    constructor(
        private http: HttpClient,
        private translateService: TranslateService
    ) {}

    // nominated agents
    getNominatedAgents(filter: any) {
        return this.http.post<any>(`api:///NominatedAgent/GetNominatedAgents`, filter);
    }

    getAddCargoAgents(id: number) {
        return this.http.get<any>(`api:///NominatedAgent/GetAddCargoAgents/${id}`);
    }

    getCargoAgentsForNomination(
        vesselVisitId: number,
        direction: string,
        relationOrganizationToOrganizationTypeId: number,
        vesselTypeId: string,
        systemId: string
    ) {
        return this.http.get<any>(
            `api:///NominatedAgent/GetCargoAgentsForNomination/${vesselVisitId}/${direction}/${relationOrganizationToOrganizationTypeId}/${vesselTypeId}/${systemId}`
        );
    }

    getCodeListsForNominatedAgentsGrid(vesselVisitId: number) {
        return this.http.get<any>(`api:///NominatedAgent/GetCodeListsForNominatedAgentsGrid/${vesselVisitId}`);
    }

    AddNominatedAgentOrganizations(model: NominatedAgentsSaveOrganizationsSaveModel) {
        return this.http.post<any>(`api:///NominatedAgent/AddNominatedAgentOrganizations`, model);
    }

    RemoveNominatedAgents(model: any) {
        return this.http.post(`api:///NominatedAgent/RemoveNominatedAgents`, model);
    }

    // shipping lines
    getShippingLines(nominatedAgentId: number) {
        return this.http.get<any>(`api:///NominatedAgent/GetShippingLines/${nominatedAgentId}`);
    }

    getCodeListsForShippingLines(nominatedAgentId: number) {
        return this.http.get<any>(`api:///NominatedAgent/GetCodeListsForShippingLines/${nominatedAgentId}`);
    }

    addNewShippingLines(model: any) {
        return this.http.post<any>(`api:///NominatedAgent/AddNewShippingLines`, model);
    }

    removeShippingLines(model: any) {
        return this.http.post<any>(`api:///NominatedAgent/RemoveShippingLines`, model);
    }

    // non containerized cargo
    getCodeListsForNonContainerized() {
        // uses the same filters as nominated agents
        return this.http.get<any>(`api:///NominatedAgent/GetCodeListsForNominatedAgentsGrid`);
    }

    getNonContainerized(vesselVisitId: number) {
        return this.http.get<any>(`api:///NominatedAgent/GetNonContainerized/${vesselVisitId}`);
    }

    saveTosVoyage(model: any) {
        return this.http.post<any>(`api:///NominatedAgent/SaveTosVoyage`, model);
    }

    // Cargo List
    getCargoManifest(cargoManifestId) {
        return this.http.get<any>(`api:///NominatedAgent/GetCargoManifest/${cargoManifestId}`);
    }

    getOrganizationsForCargoManifest(cargoManifestId) {
        return this.http.get<any>(`api:///NominatedAgent/GetOrganizationsForCargoManifest/${cargoManifestId}`);
    }

    addCargoManifestAttachment(model: any) {
        return this.http.post<any>(`api:///NominatedAgent/AddCargoManifestAttachment`, model);
    }

    removeCargoManifestAttachment(model: any) {
        return this.http.post<any>(`api:///NominatedAgent/RemoveCargoManifestAttachment`, model);
    }

    downloadCargoManifestAttachment(attachmentId: number) {
        return this.http.get<any>(`api:///NominatedAgent/DownloadCargoManifestAttachment/${attachmentId}`);
    }

    getCargoManifestItems(filter: any) {
        return this.http.post<any>(`api:///NominatedAgent/GetCargoManifestItems`, filter);
    }

    getCargoManifestInvalidItems(manifestId: number) {
        return this.http.get<any>(`api:///NominatedAgent/GetManifestInvalidRows/${manifestId}`);
    }

    saveCargoManifestItem(model: any) {
        return this.http.post<any>(`api:///NominatedAgent/SaveCargoManifestItem`, model);
    }

    getCodeListsForManifestItems(id) {
        return this.http.get<any>(`api:///NominatedAgent/ManifestItemsCodeListValues/${id}`);
    }

    removeManifestRow(ids) {
        return this.http.post<any>(`api:///NominatedAgent/RemoveManifestItemRow`, ids);
    }

    approveCargoManifest(id) {
        return this.http.get(`api:///NominatedAgent/ApproveCargoManifest/${id}`);
    }

    finishCargoList(id) {
        return this.http.get(`api:///NominatedAgent/FinishCargoList/${id}`);
    }

    getServiceRequestsForCargoManifest(vesselVisitId: number, cargoManifestId: number, forContainers: boolean) {
        return this.http.get<any>(
            `api:///NominatedAgent/ServiceRequestsForCargoManifest/${vesselVisitId}/${cargoManifestId}/${forContainers}`
        );
    }

    getCargoManifestItemIds(filter: any) {
        return this.http.post<any>(`api:///NominatedAgent/GetCargoManifestItemIds`, filter);
    }

    getCargoManifestPrintData(filter: any) {
        return this.http.post<any>(`api:///NominatedAgent/GetCargoManifestPrintData`, filter);
    }

    getCargoManifestVehiclesList(filter: any) {
        return this.http.post<any>(`api:///NominatedAgent/GetCargoManifestVehiclesList/`, filter);
    }

    getEditDataForManifestVehicle(cargoManifestItemVehicleId: any): Observable<any> {
        return this.http.get<any>(`api:///NominatedAgent/GetEditDataForManifestVehicle/${cargoManifestItemVehicleId}`);
    }

    getManifestVehicleLinkedUnitOptions(filter: {
        cargoManifestId: number;
        currentVehicleId?: number;
    }): Observable<any> {
        return this.http.post<any>(`api:///NominatedAgent/GetManifestVehicleLinkedUnitOptions`, filter);
    }

    saveCargoManifestItemVehicle(model: any) {
        return this.http.post<any>(`api:///NominatedAgent/SaveCargoManifestItemVehicle`, model);
    }

    removeCargoManifestItemVehicles(model: any) {
        return this.http.post<any>(`api:///NominatedAgent/RemoveCargoManifestItemVehicles`, model);
    }

    correctFromDraftSurvey(cargoManifestId: number) {
        return this.http.post<any>(`api:///NominatedAgent/CorrectFromDraftSurvey`, { cargoManifestId });
    }

    getNominatedAgentShipmentTypeIconAndTooltip(
        containers: boolean,
        roro: boolean
    ): { icon: IconProp; tooltip: string } {
        if (containers) {
            return {
                icon: ['custom', 'full-container'] as any,
                tooltip: this.translateService.instant(marker('Containers'))
            };
        }
        if (roro) {
            return { icon: ['fas', 'car'], tooltip: this.translateService.instant(marker('Ro-Ro Cargo')) };
        }
        return { icon: ['fas', 'cubes'], tooltip: this.translateService.instant(marker('Bulk Cargo')) };
    }
}
